'use client';

import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Divider,
  CircularProgress,
  Stack,
  IconButton,
  Avatar,
  InputAdornment
} from '@mui/material';

import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import EditIcon from '@mui/icons-material/Edit';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';

import { getUserById, getUserImage, uploadUserFile, updateUser, fetchUserById } from '../../stores/masterUserStore';
import { useNotifier } from '../../components/Notifier';
import { useLanguage } from '../../utils/i18n';

export default function ProfilePage() {
  const notify = useNotifier();
  const { t } = useLanguage();

  const [userId, setUserId] = useState(null);

  const [userData, setUserData] = useState(null);
  const [userDataDetail, setUserDataDetail] = useState(null);

  const [profileImage, setProfileImage] = useState(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [passwords, setPasswords] = useState({
    oldPassword: '',
    newPassword: '',
    retypePassword: ''
  });
  const [showPassword, setShowPassword] = useState({
    old: false,
    new: false,
    retype: false
  });

  const fileInputRef = useRef(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const storedId = localStorage.getItem('user_id');
      if (storedId) {
        setUserId(storedId);
      } else {
        console.warn('User ID tidak ditemukan di localStorage');
      }
    }
  }, []);

  useEffect(() => {
    const fetchAllData = async () => {
      if (!userId) return;
      setIsLoading(true);
      try {
        const [fetchedData, fetchedDetail] = await Promise.all([fetchUserById(userId), getUserById(userId)]);

        setUserData(fetchedData);
        setUserDataDetail(fetchedDetail);

        try {
          const imageBlob = await getUserImage(userId, true);
          if (imageBlob && imageBlob.size > 0) {
            const imageUrl = URL.createObjectURL(imageBlob);
            setProfileImage(imageUrl);
          }
        } catch {
          console.log('User belum memiliki gambar profil atau gambar gagal dimuat.');
        }
      } catch {
        notify({ message: t('profile.errLoadProfile', 'Failed to load profile data.'), severity: 'error' });
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllData();

    return () => {
      if (profileImage) URL.revokeObjectURL(profileImage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file || !userId) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await uploadUserFile(userId, formData);
      notify({ message: t('profile.succUpdateImage', 'Profile image updated successfully!'), severity: 'success' });

      const newImageBlob = await getUserImage(userId, true);
      if (newImageBlob) {
        if (profileImage) URL.revokeObjectURL(profileImage);
        setProfileImage(URL.createObjectURL(newImageBlob));
      }
    } catch {
      notify({ message: t('profile.errUploadImage', 'Failed to upload image.'), severity: 'error' });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswords((prev) => ({ ...prev, [name]: value }));
  };

  const handleTogglePasswordVisibility = (field) => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const submitChangePassword = async () => {
    if (!passwords.oldPassword || !passwords.newPassword || !passwords.retypePassword) {
      notify({ message: t('profile.errFieldsRequired', 'All password fields are required.'), severity: 'warning' });
      return;
    }
    if (passwords.newPassword !== passwords.retypePassword) {
      notify({ message: t('profile.errPasswordMismatch', 'New password and Retype password do not match!'), severity: 'error' });
      return;
    }

    setIsSaving(true);
    try {
      await updateUser(userId, {
        old_password: passwords.oldPassword,
        password: passwords.newPassword
      });

      notify({ message: t('profile.succChangePassword', 'Password successfully changed!'), severity: 'success' });
      setPasswords({ oldPassword: '', newPassword: '', retypePassword: '' });
    } catch {
      notify({ message: t('profile.errChangePassword', 'Failed to change password. Please check your old password.'), severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress sx={{ color: '#485D9A' }} />
      </Box>
    );
  }

  const companyName = userData?.join?.company_name || userData?.fullname || t('general.company', 'Company');
  const companyAddress = userData?.join?.company_address || t('profile.addressNotSet', 'Address not set');
  const companyPhone = userData?.join?.company_phone1 || userData?.join?.company_pic_mobile_no || '-';
  const companyFax = userData?.join?.company_fax1 || '-';
  const companyWeb = userData?.join?.company_website || '-';

  const lastSignIn =
    userDataDetail?.last_login_date ||
    userDataDetail?.last_login ||
    userData?.last_login_date ||
    t('profile.neverLoggedIn', 'Never logged in');

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 3,
          width: '100%',
          alignItems: 'stretch'
        }}
      >
        <Box
          sx={{
            flexBasis: { xs: '100%', md: '25%' },
            flexShrink: 0,
            maxWidth: { xs: '100%', md: '350px' }
          }}
        >
          <Card
            elevation={0}
            sx={{
              borderRadius: 4,
              border: '1px solid #E5EAF2',
              boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
              height: '100%'
            }}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: 220,
                  aspectRatio: '1 / 1',
                  mx: 'auto',
                  mb: 3,
                  borderRadius: 3,
                  overflow: 'hidden',
                  bgcolor: '#F3F6F9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px dashed #D0D7E2'
                }}
              >
                {isUploading ? (
                  <CircularProgress sx={{ color: '#485D9A' }} />
                ) : profileImage ? (
                  <img src={profileImage} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <Stack spacing={1} alignItems="center" sx={{ color: '#9AA4B2' }}>
                    <PhotoCameraIcon sx={{ fontSize: 48, opacity: 0.5 }} />
                    <Typography variant="body2" fontWeight="500">
                      {t('profile.imgNotAvailable', 'Image not available')}
                    </Typography>
                  </Stack>
                )}
              </Box>

              <Button
                variant="contained"
                fullWidth
                disabled={isUploading}
                startIcon={<EditIcon />}
                onClick={() => fileInputRef.current.click()}
                sx={{
                  bgcolor: '#485D9A',
                  borderRadius: 1,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 'bold',
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#3B4D82', boxShadow: '0 4px 12px rgba(72, 93, 154, 0.3)' }
                }}
              >
                {isUploading ? t('buttonTitle.uploading', 'Uploading...') : t('profile.editImage', 'Edit Image')}
              </Button>
              <input type="file" accept="image/*" hidden ref={fileInputRef} onChange={handleImageUpload} />

              <Divider sx={{ my: 1 }} />

              <Box sx={{ textAlign: 'left' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="800" sx={{ color: '#2B3445', lineHeight: 1.2 }}>
                    {companyName}
                  </Typography>
                  <IconButton size="small" sx={{ color: '#485D9A', bgcolor: 'rgba(72,93,154,0.1)' }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Stack spacing={2.5}>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    <Avatar sx={{ bgcolor: 'transparent', color: '#485D9A', width: 24, height: 24 }}>
                      <LocationOnOutlinedIcon fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="caption" fontWeight="bold" color="text.secondary">
                        {t('profile.address', 'Address :')}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#3A4251', mt: 0.5, lineHeight: 1.4 }}>
                        {companyAddress}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    <Avatar sx={{ bgcolor: 'transparent', color: '#485D9A', width: 24, height: 24 }}>
                      <PhoneOutlinedIcon fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="caption" fontWeight="bold" color="text.secondary">
                        {t('profile.phone', 'Phone :')}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#3A4251', mt: 0.2 }}>
                        {companyPhone}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    <Avatar sx={{ bgcolor: 'transparent', color: '#485D9A', width: 24, height: 24 }}>
                      <PrintOutlinedIcon fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="caption" fontWeight="bold" color="text.secondary">
                        {t('profile.fax', 'Fax :')}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#3A4251', mt: 0.2 }}>
                        {companyFax}
                      </Typography>
                    </Box>
                  </Box>
                  {companyWeb && companyWeb !== '-' && (
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                      <Avatar sx={{ bgcolor: 'transparent', color: '#485D9A', width: 24, height: 24 }}>
                        <LanguageOutlinedIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="caption" fontWeight="bold" color="text.secondary">
                          {t('profile.website', 'Website :')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#3A4251', mt: 0.2 }}>
                          {companyWeb}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            flexBasis: { xs: '100%', md: '75%' },
            minWidth: 0
          }}
        >
          <Stack spacing={3} sx={{ height: '100%' }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 4,
                border: '1px solid #E5EAF2',
                bgcolor: '#FAFBFC',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: { xs: 2, sm: 3 }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: '#485D9A', color: 'white' }}>
                  <AccessTimeIcon />
                </Avatar>
                <Box>
                  <Typography variant="body2" fontWeight="bold" color="text.secondary">
                    {t('profile.lastSignIn', 'Last Sign In')}
                  </Typography>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#2B3445' }}>
                    {lastSignIn && lastSignIn !== t('profile.neverLoggedIn', 'Never logged in')
                      ? new Date(lastSignIn).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'medium' })
                      : lastSignIn}
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="outlined"
                size="small"
                sx={{
                  borderRadius: 6,
                  textTransform: 'none',
                  borderColor: '#485D9A',
                  color: '#485D9A',
                  fontWeight: 'bold',
                  '&:hover': { bgcolor: 'rgba(72, 93, 154, 0.05)', borderColor: '#485D9A' }
                }}
              >
                {t('profile.viewDetail', 'View Detail Info')}
              </Button>
            </Card>
            <Card
              elevation={0}
              sx={{
                borderRadius: 4,
                border: '1px solid #E5EAF2',
                boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
                flexGrow: 1
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
                <Typography variant="h6" fontWeight="bold" sx={{ color: '#2B3445', mb: 4 }}>
                  {t('profile.changePassword', 'Change Password')}
                </Typography>

                <Stack spacing={3}>
                  <TextField
                    label={t('profile.oldPassword', 'Old Password')}
                    name="oldPassword"
                    type={showPassword.old ? 'text' : 'password'}
                    value={passwords.oldPassword}
                    onChange={handlePasswordChange}
                    fullWidth
                    variant="standard"
                    InputLabelProps={{ shrink: true, sx: { fontSize: 18, color: '#9AA4B2' } }}
                    InputProps={{
                      sx: { pb: 1, fontSize: 15, fontWeight: 500, color: '#2B3445' },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => handleTogglePasswordVisibility('old')} edge="end">
                            {showPassword.old ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />

                  <TextField
                    label={t('profile.newPassword', 'New Password')}
                    name="newPassword"
                    type={showPassword.new ? 'text' : 'password'}
                    value={passwords.newPassword}
                    onChange={handlePasswordChange}
                    fullWidth
                    variant="standard"
                    InputLabelProps={{ shrink: true, sx: { fontSize: 18, color: '#9AA4B2' } }}
                    InputProps={{
                      sx: { pb: 1, fontSize: 15, fontWeight: 500, color: '#2B3445' },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => handleTogglePasswordVisibility('new')} edge="end">
                            {showPassword.new ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />

                  <TextField
                    label={t('profile.retypePassword', 'Retype New Password')}
                    name="retypePassword"
                    type={showPassword.retype ? 'text' : 'password'}
                    value={passwords.retypePassword}
                    onChange={handlePasswordChange}
                    fullWidth
                    variant="standard"
                    error={passwords.retypePassword !== '' && passwords.newPassword !== passwords.retypePassword}
                    helperText={
                      passwords.retypePassword !== '' && passwords.newPassword !== passwords.retypePassword
                        ? t('profile.passwordMismatch', 'Passwords do not match')
                        : ''
                    }
                    InputLabelProps={{ shrink: true, sx: { fontSize: 18, color: '#9AA4B2' } }}
                    InputProps={{
                      sx: { pb: 1, fontSize: 15, fontWeight: 500, color: '#2B3445' },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => handleTogglePasswordVisibility('retype')} edge="end">
                            {showPassword.retype ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />

                  <Box sx={{ pt: 3 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      disabled={isSaving}
                      onClick={submitChangePassword}
                      sx={{
                        bgcolor: '#485D9A',
                        borderRadius: 1,
                        py: 1,
                        textTransform: 'none',
                        fontWeight: 'bold',
                        boxShadow: 'none',
                        '&:hover': { bgcolor: '#3B4D82', boxShadow: '0 4px 12px rgba(72, 93, 154, 0.3)' }
                      }}
                    >
                      {isSaving ? <CircularProgress color="inherit" /> : t('profile.saveChanges', 'Save Changes')}
                    </Button>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
